// Treatment content collection — drives the reusable TreatmentPage template.
// Each treatment renders a dedicated, indexable SEO page in EN / FR / DE.
// Content is independent, educational and non-promotional (YMYL healthcare).

export const TREATMENTS = [
  {
    key: "dental-implants",
    icon: "Anchor",
    featured: true,
    image:
      "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "dental-implants-vietnam",
      fr: "implants-dentaires-vietnam",
      de: "zahnimplantate-vietnam",
    },
    name: { en: "Dental Implants", fr: "Implants dentaires", de: "Zahnimplantate" },
    tagline: {
      en: "A long-term option for replacing missing teeth.",
      fr: "Une option durable pour remplacer les dents manquantes.",
      de: "Eine langfristige Option für fehlende Zähne.",
    },
    metaTitle: {
      en: "Dental Implants in Vietnam | Guide for International Patients",
      fr: "Implants dentaires au Vietnam | Guide pour patients internationaux",
      de: "Zahnimplantate in Vietnam | Ratgeber für internationale Patienten",
    },
    metaDesc: {
      en: "Independent information on dental implants in Vietnam for international patients: what they involve, what to check and how the process works.",
      fr: "Informations indépendantes sur les implants dentaires au Vietnam : déroulement, points à vérifier et fonctionnement du processus.",
      de: "Unabhängige Informationen zu Zahnimplantaten in Vietnam: Ablauf, Prüfpunkte und Prozess für internationale Patienten.",
    },
    overview: {
      en: [
        "A dental implant is a small titanium post placed in the jawbone to support a replacement tooth, bridge or full arch of teeth. Implants are widely used to replace one or more missing teeth and can offer a stable, long-term solution.",
        "Whether an implant is suitable for you depends on factors such as bone quality, gum health and general health. Only a licensed dentist can determine suitability after an in-person examination and appropriate imaging.",
      ],
      fr: [
        "Un implant dentaire est une petite vis en titane placée dans l’os de la mâchoire pour soutenir une dent de remplacement, un bridge ou une arcade complète. Les implants sont largement utilisés pour remplacer une ou plusieurs dents manquantes et offrent une solution stable et durable.",
        "L’indication d’un implant dépend de facteurs tels que la qualité osseuse, la santé gingivale et l’état de santé général. Seul un dentiste agréé peut juger de l’indication après un examen en personne et une imagerie adaptée.",
      ],
      de: [
        "Ein Zahnimplantat ist eine kleine Titanschraube, die in den Kieferknochen eingesetzt wird und einen Ersatzzahn, eine Brücke oder einen ganzen Zahnbogen trägt. Implantate werden häufig für den Ersatz eines oder mehrerer Zähne eingesetzt und bieten eine stabile, langfristige Lösung.",
        "Ob ein Implantat für Sie geeignet ist, hängt von Faktoren wie Knochenqualität, Zahnfleischgesundheit und allgemeinem Gesundheitszustand ab. Nur ein lizenzierter Zahnarzt kann die Eignung nach einer persönlichen Untersuchung und geeigneter Bildgebung beurteilen.",
      ],
    },
    involves: {
      en: [
        "Consultation, imaging (such as CBCT) and treatment planning",
        "Placement of the titanium implant into the jawbone",
        "A healing period while the implant integrates with the bone",
        "Attachment of an abutment and final crown, bridge or denture",
      ],
      fr: [
        "Consultation, imagerie (comme le CBCT) et planification du traitement",
        "Pose de l’implant en titane dans l’os de la mâchoire",
        "Une période de cicatrisation pendant l’ostéo-intégration",
        "Pose d’un pilier puis de la couronne, du bridge ou de la prothèse finale",
      ],
      de: [
        "Beratung, Bildgebung (z. B. CBCT) und Behandlungsplanung",
        "Einsetzen des Titanimplantats in den Kieferknochen",
        "Eine Einheilphase, während das Implantat einwächst",
        "Anbringen eines Aufbaus und der finalen Krone, Brücke oder Prothese",
      ],
    },
    benefits: {
      en: [
        "Designed to look and function like a natural tooth",
        "Helps preserve jawbone where teeth are missing",
        "Does not rely on grinding down neighbouring teeth",
        "A durable option when well maintained",
      ],
      fr: [
        "Conçu pour ressembler et fonctionner comme une dent naturelle",
        "Aide à préserver l’os de la mâchoire là où des dents manquent",
        "Ne nécessite pas de tailler les dents voisines",
        "Une option durable avec un bon entretien",
      ],
      de: [
        "Sieht aus und funktioniert wie ein natürlicher Zahn",
        "Hilft, den Kieferknochen bei fehlenden Zähnen zu erhalten",
        "Erfordert kein Beschleifen der Nachbarzähne",
        "Bei guter Pflege eine langlebige Option",
      ],
    },
    faqs: [
      {
        q: {
          en: "Are dental implants in Vietnam safe?",
          fr: "Les implants dentaires au Vietnam sont-ils sûrs ?",
          de: "Sind Zahnimplantate in Vietnam sicher?",
        },
        a: {
          en: "Safety depends on the individual clinic, the dentist’s training and the standards followed. We encourage patients to check the clinic’s licence, the dentist’s credentials, sterilisation procedures and the implant brand used.",
          fr: "La sécurité dépend de la clinique, de la formation du dentiste et des standards suivis. Nous encourageons les patients à vérifier la licence de la clinique, les diplômes du dentiste, les procédures de stérilisation et la marque d’implant utilisée.",
          de: "Die Sicherheit hängt von der jeweiligen Klinik, der Ausbildung des Zahnarztes und den eingehaltenen Standards ab. Wir empfehlen, Kliniklizenz, Qualifikationen, Sterilisationsverfahren und die verwendete Implantatmarke zu prüfen.",
        },
      },
      {
        q: {
          en: "How long does implant treatment take?",
          fr: "Combien de temps dure le traitement implantaire ?",
          de: "Wie lange dauert die Implantatbehandlung?",
        },
        a: {
          en: "Timelines vary. Implants often heal over several months before the final tooth is placed, so treatment may involve more than one visit. Your dentist will explain the timeline for your case.",
          fr: "Les délais varient. Les implants cicatrisent souvent sur plusieurs mois avant la pose de la dent finale ; le traitement peut donc comporter plusieurs visites. Votre dentiste vous précisera le calendrier.",
          de: "Die Zeitpläne variieren. Implantate heilen oft über mehrere Monate, bevor der finale Zahn gesetzt wird, sodass mehrere Besuche nötig sein können. Ihr Zahnarzt erläutert den Zeitplan.",
        },
      },
      {
        q: {
          en: "How much do dental implants cost in Vietnam?",
          fr: "Combien coûtent les implants dentaires au Vietnam ?",
          de: "Was kosten Zahnimplantate in Vietnam?",
        },
        a: {
          en: "Costs depend on the number of implants, the brand, any additional procedures and the clinic. We do not publish fixed prices; please request indicative information for your specific case.",
          fr: "Le coût dépend du nombre d’implants, de la marque, des procédures complémentaires et de la clinique. Nous ne publions pas de prix fixes ; demandez une information indicative pour votre cas.",
          de: "Die Kosten hängen von der Anzahl der Implantate, der Marke, zusätzlichen Eingriffen und der Klinik ab. Wir veröffentlichen keine festen Preise; bitte fordern Sie Richtinformationen für Ihren Fall an.",
        },
      },
    ],
    sections: [
      {
        h: {
          en: "Types of dental implants",
          fr: "Types d’implants dentaires",
          de: "Arten von Zahnimplantaten",
        },
        list: {
          en: [
            "Single implants — to replace one missing tooth without altering neighbouring teeth",
            "Multiple implants — to replace several teeth, sometimes with an implant-supported bridge",
            "Full-mouth implants — to restore a complete arch, including All-on-4 and All-on-6 solutions",
          ],
          fr: [
            "Implant unitaire — remplacer une dent manquante sans toucher aux dents voisines",
            "Implants multiples — remplacer plusieurs dents, parfois avec un bridge sur implants",
            "Implants pour bouche complète — restaurer une arcade entière, dont l’All-on-4 et l’All-on-6",
          ],
          de: [
            "Einzelimplantate — Ersatz eines fehlenden Zahns ohne Beschleifen der Nachbarzähne",
            "Mehrere Implantate — Ersatz mehrerer Zähne, teils mit implantatgetragener Brücke",
            "Vollkiefer-Implantate — Wiederherstellung eines ganzen Bogens, inkl. All-on-4 und All-on-6",
          ],
        },
      },
      {
        h: {
          en: "Imaging, implant brands and technology",
          fr: "Imagerie, marques d’implants et technologie",
          de: "Bildgebung, Implantatmarken und Technik",
        },
        body: {
          en: [
            "Accurate planning often uses CBCT (3D) imaging to assess bone volume and position implants safely. Selected providers may work with internationally recognised implant systems — confirm the exact brand and materials directly with the clinic, and ask for documentation.",
          ],
          fr: [
            "Une planification précise s’appuie souvent sur l’imagerie CBCT (3D) pour évaluer le volume osseux et positionner les implants en sécurité. Certains prestataires sélectionnés utilisent des systèmes d’implants reconnus — confirmez la marque exacte et les matériaux directement auprès de la clinique et demandez la documentation.",
          ],
          de: [
            "Eine präzise Planung nutzt oft CBCT-(3D)-Bildgebung, um das Knochenvolumen zu beurteilen und Implantate sicher zu positionieren. Ausgewählte Anbieter arbeiten ggf. mit anerkannten Implantatsystemen — bestätigen Sie Marke und Materialien direkt bei der Klinik und bitten Sie um Dokumentation.",
          ],
        },
      },
      {
        h: {
          en: "Treatment stages and healing",
          fr: "Étapes du traitement et cicatrisation",
          de: "Behandlungsschritte und Heilung",
        },
        body: {
          en: [
            "Treatment usually begins with a consultation and imaging, followed by implant placement. The implant then integrates with the bone over a healing period — often several months — before the final crown, bridge or arch is attached. Because of this, treatment may be staged across more than one visit to Vietnam.",
          ],
          fr: [
            "Le traitement débute généralement par une consultation et une imagerie, suivies de la pose de l’implant. L’implant s’intègre ensuite à l’os pendant une cicatrisation — souvent plusieurs mois — avant la pose de la couronne, du bridge ou de l’arcade finale. Le traitement peut donc s’échelonner sur plusieurs visites au Vietnam.",
          ],
          de: [
            "Die Behandlung beginnt meist mit Beratung und Bildgebung, gefolgt vom Einsetzen des Implantats. Das Implantat wächst dann über eine Heilphase — oft mehrere Monate — ein, bevor Krone, Brücke oder Bogen befestigt werden. Daher kann die Behandlung über mehrere Vietnam-Besuche verteilt werden.",
          ],
        },
      },
      {
        h: {
          en: "Bone grafting and sinus lift",
          fr: "Greffe osseuse et élévation du sinus",
          de: "Knochenaufbau und Sinuslift",
        },
        body: {
          en: [
            "If there is not enough bone to support an implant, a dentist may discuss bone grafting, or a sinus lift in the upper jaw. Whether these are needed can only be determined by a licensed dentist after imaging and an in-person assessment. They can add time and cost to treatment.",
          ],
          fr: [
            "S’il n’y a pas assez d’os pour soutenir un implant, le dentiste peut évoquer une greffe osseuse, ou une élévation du sinus au maxillaire. Leur nécessité ne peut être déterminée que par un dentiste agréé après imagerie et examen en personne. Elles peuvent allonger la durée et augmenter le coût.",
          ],
          de: [
            "Reicht der Knochen nicht aus, kann der Zahnarzt einen Knochenaufbau oder im Oberkiefer einen Sinuslift besprechen. Ob dies nötig ist, kann nur ein lizenzierter Zahnarzt nach Bildgebung und persönlicher Untersuchung feststellen. Beides kann Dauer und Kosten erhöhen.",
          ],
        },
      },
      {
        h: {
          en: "Risks and considerations",
          fr: "Risques et points à considérer",
          de: "Risiken und Überlegungen",
        },
        body: {
          en: [
            "As with any procedure, implants carry risks, and outcomes are not guaranteed. Careful case selection, good planning and aftercare reduce risk. Travelling for treatment also means follow-up can be harder to arrange from abroad, so understand the aftercare and warranty terms before you commit.",
          ],
          fr: [
            "Comme toute intervention, les implants comportent des risques et les résultats ne sont pas garantis. Une bonne sélection des cas, une planification soignée et un suivi réduisent le risque. Se déplacer pour un traitement rend aussi le suivi plus difficile à organiser à distance : comprenez le suivi et la garantie avant de vous engager.",
          ],
          de: [
            "Wie bei jedem Eingriff bergen Implantate Risiken, und Ergebnisse sind nicht garantiert. Sorgfältige Fallauswahl, gute Planung und Nachsorge senken das Risiko. Eine Behandlungsreise erschwert zudem die Nachsorge aus der Ferne: Klären Sie Nachsorge und Garantie vor der Entscheidung.",
          ],
        },
      },
      {
        h: {
          en: "Choosing a clinic and travelling to Vietnam",
          fr: "Choisir une clinique et voyager au Vietnam",
          de: "Klinikwahl und Reise nach Vietnam",
        },
        list: {
          en: [
            "Ask about the dentist’s implant experience and how many cases they handle",
            "Confirm the implant brand, materials, warranty and written treatment plan",
            "Check sterilisation and infection-control procedures",
            "Plan your stay around the healing timeline and any follow-up visits",
            "Clarify aftercare once you return home, and who to contact with questions",
          ],
          fr: [
            "Renseignez-vous sur l’expérience du dentiste en implantologie et le nombre de cas traités",
            "Confirmez la marque d’implant, les matériaux, la garantie et le plan de traitement écrit",
            "Vérifiez les procédures de stérilisation et de contrôle des infections",
            "Planifiez votre séjour selon la cicatrisation et les visites de suivi",
            "Clarifiez le suivi une fois rentré et qui contacter en cas de question",
          ],
          de: [
            "Fragen Sie nach der Implantaterfahrung des Zahnarztes und der Fallzahl",
            "Bestätigen Sie Implantatmarke, Materialien, Garantie und schriftlichen Behandlungsplan",
            "Prüfen Sie Sterilisations- und Infektionskontrollverfahren",
            "Planen Sie den Aufenthalt nach Heildauer und Nachsorgeterminen",
            "Klären Sie die Nachsorge nach der Rückkehr und Ansprechpartner bei Fragen",
          ],
        },
      },
    ],
    related: ["all-on-4", "all-on-6", "dental-crowns", "dental-bridges"],
  },

  {
    key: "all-on-4",
    icon: "Grip",
    featured: true,
    image:
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "all-on-4-dental-implants-vietnam",
      fr: "all-on-4-implants-dentaires-vietnam",
      de: "all-on-4-zahnimplantate-vietnam",
    },
    name: { en: "All-on-4 Dental Implants", fr: "Implants All-on-4", de: "All-on-4 Implantate" },
    tagline: {
      en: "A full arch of teeth supported by four implants.",
      fr: "Une arcade complète soutenue par quatre implants.",
      de: "Ein ganzer Zahnbogen auf vier Implantaten.",
    },
    metaTitle: {
      en: "All-on-4 Dental Implants in Vietnam | Patient Guide",
      fr: "Implants All-on-4 au Vietnam | Guide du patient",
      de: "All-on-4 Zahnimplantate in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "What All-on-4 dental implants involve for international patients considering treatment in Vietnam, and what to check before you choose.",
      fr: "En quoi consistent les implants All-on-4 pour les patients internationaux au Vietnam, et ce qu’il faut vérifier avant de choisir.",
      de: "Was All-on-4 Implantate für internationale Patienten in Vietnam bedeuten und was Sie vor der Wahl prüfen sollten.",
    },
    overview: {
      en: [
        "All-on-4 is a technique that supports a full arch of replacement teeth using four dental implants. It is often considered by patients who are missing most or all of their teeth in one jaw.",
        "Suitability depends on your bone and gum health and must be assessed by a licensed dentist. The approach and materials can vary between clinics.",
      ],
      fr: [
        "L’All-on-4 est une technique qui soutient une arcade complète de dents de remplacement à l’aide de quatre implants. Elle est souvent envisagée par les patients ayant perdu la plupart ou la totalité de leurs dents sur une mâchoire.",
        "L’indication dépend de l’état osseux et gingival et doit être évaluée par un dentiste agréé. L’approche et les matériaux varient selon les cliniques.",
      ],
      de: [
        "All-on-4 ist eine Technik, die einen ganzen Zahnbogen mit vier Implantaten trägt. Sie wird oft von Patienten in Betracht gezogen, denen die meisten oder alle Zähne eines Kiefers fehlen.",
        "Die Eignung hängt von Knochen- und Zahnfleischgesundheit ab und muss von einem lizenzierten Zahnarzt beurteilt werden. Vorgehen und Materialien variieren je nach Klinik.",
      ],
    },
    involves: {
      en: [
        "Detailed assessment and 3D imaging of the jaw",
        "Placement of four implants at planned angles",
        "A provisional set of teeth in many cases",
        "A final fixed bridge once healing is complete",
      ],
      fr: [
        "Évaluation détaillée et imagerie 3D de la mâchoire",
        "Pose de quatre implants selon des angles planifiés",
        "Un bridge provisoire dans de nombreux cas",
        "Un bridge fixe définitif une fois la cicatrisation terminée",
      ],
      de: [
        "Detaillierte Beurteilung und 3D-Bildgebung des Kiefers",
        "Setzen von vier Implantaten in geplanten Winkeln",
        "In vielen Fällen ein provisorisches Gebiss",
        "Eine finale festsitzende Brücke nach der Einheilung",
      ],
    },
    benefits: {
      en: [
        "Can replace a full arch of missing teeth",
        "A fixed, non-removable set of teeth in many cases",
        "May reduce the need for extensive bone grafting",
        "Designed to restore chewing and appearance",
      ],
      fr: [
        "Peut remplacer une arcade complète de dents manquantes",
        "Des dents fixes et non amovibles dans de nombreux cas",
        "Peut réduire le besoin de greffes osseuses importantes",
        "Conçu pour restaurer la mastication et l’esthétique",
      ],
      de: [
        "Kann einen ganzen fehlenden Zahnbogen ersetzen",
        "In vielen Fällen festsitzende, nicht herausnehmbare Zähne",
        "Kann umfangreiche Knochenaufbauten reduzieren",
        "Soll Kaufunktion und Aussehen wiederherstellen",
      ],
    },
    faqs: [
      {
        q: {
          en: "Is All-on-4 available in Vietnam?",
          fr: "L’All-on-4 est-il disponible au Vietnam ?",
          de: "Ist All-on-4 in Vietnam verfügbar?",
        },
        a: {
          en: "Some selected providers in Vietnam offer full-arch implant treatments such as All-on-4. Availability, systems and experience vary by clinic — confirm details directly with the provider.",
          fr: "Certains prestataires sélectionnés au Vietnam proposent des traitements implantaires d’arcade complète comme l’All-on-4. La disponibilité, les systèmes et l’expérience varient selon la clinique — confirmez directement.",
          de: "Einige ausgewählte Anbieter in Vietnam bieten Vollbogen-Implantatbehandlungen wie All-on-4 an. Verfügbarkeit, Systeme und Erfahrung variieren — bitte direkt bestätigen.",
        },
      },
      {
        q: {
          en: "How many visits does All-on-4 require?",
          fr: "Combien de visites l’All-on-4 nécessite-t-il ?",
          de: "Wie viele Besuche erfordert All-on-4?",
        },
        a: {
          en: "This varies by case and clinic. Some treatment can be staged across visits with a healing period in between. Your dentist will outline the plan for you.",
          fr: "Cela varie selon le cas et la clinique. Le traitement peut être échelonné avec une période de cicatrisation. Votre dentiste vous détaillera le plan.",
          de: "Das hängt von Fall und Klinik ab. Die Behandlung kann mit einer Einheilphase auf mehrere Besuche verteilt werden. Ihr Zahnarzt erläutert den Plan.",
        },
      },
      {
        q: {
          en: "What should I check before All-on-4 treatment?",
          fr: "Que vérifier avant un traitement All-on-4 ?",
          de: "Was sollte ich vor einer All-on-4-Behandlung prüfen?",
        },
        a: {
          en: "Check the implant brand, the materials used for the final teeth, the dentist’s experience with full-arch cases, warranty terms and the aftercare plan.",
          fr: "Vérifiez la marque d’implant, les matériaux des dents finales, l’expérience du dentiste en arcade complète, les conditions de garantie et le suivi.",
          de: "Prüfen Sie Implantatmarke, Materialien der finalen Zähne, die Erfahrung des Zahnarztes mit Vollbogen-Fällen, Garantiebedingungen und Nachsorge.",
        },
      },
    ],
    sections: [
      {
        h: {
          en: "All-on-4 vs traditional implants",
          fr: "All-on-4 vs implants traditionnels",
          de: "All-on-4 vs. herkömmliche Implantate",
        },
        body: {
          en: [
            "With traditional implants, each missing tooth may need its own implant. All-on-4 instead supports a full arch of fixed teeth on just four implants, often placed at angles to make the most of available bone. This can reduce the number of implants needed and, in some cases, the need for extensive bone grafting.",
          ],
          fr: [
            "Avec les implants traditionnels, chaque dent manquante peut nécessiter son propre implant. L’All-on-4 soutient au contraire une arcade complète de dents fixes sur seulement quatre implants, souvent posés en angle pour exploiter l’os disponible. Cela peut réduire le nombre d’implants et, parfois, le besoin de greffes osseuses importantes.",
          ],
          de: [
            "Bei herkömmlichen Implantaten kann jeder fehlende Zahn ein eigenes Implantat benötigen. All-on-4 trägt dagegen einen ganzen Bogen fester Zähne auf nur vier Implantaten, die oft schräg gesetzt werden, um vorhandenen Knochen zu nutzen. Das kann die Zahl der Implantate und teils den Bedarf an umfangreichem Knochenaufbau verringern.",
          ],
        },
      },
      {
        h: {
          en: "Upper and lower arch, temporary and final teeth",
          fr: "Arcade supérieure et inférieure, dents provisoires et définitives",
          de: "Ober- und Unterkiefer, provisorische und finale Zähne",
        },
        body: {
          en: [
            "All-on-4 can be used for the upper arch, the lower arch or both. In many cases a provisional (temporary) set of teeth is fitted while the implants heal, with a stronger final prosthesis placed once integration is complete. Your dentist will explain the materials and timeline for each stage.",
          ],
          fr: [
            "L’All-on-4 peut concerner l’arcade supérieure, inférieure ou les deux. Dans de nombreux cas, un jeu de dents provisoires est posé pendant la cicatrisation, puis une prothèse définitive plus résistante une fois l’intégration terminée. Votre dentiste précisera les matériaux et le calendrier de chaque étape.",
          ],
          de: [
            "All-on-4 kann für Ober-, Unterkiefer oder beide genutzt werden. Häufig wird während der Einheilung ein provisorisches Gebiss eingesetzt und nach der Integration eine stabilere finale Prothese. Ihr Zahnarzt erläutert Materialien und Zeitplan jeder Phase.",
          ],
        },
      },
      {
        h: {
          en: "All-on-4 vs All-on-6 and additional procedures",
          fr: "All-on-4 vs All-on-6 et procédures complémentaires",
          de: "All-on-4 vs. All-on-6 und Zusatzeingriffe",
        },
        body: {
          en: [
            "All-on-6 uses six implants and may offer additional support in some cases. The right choice depends on your bone, your case and your dentist’s assessment. Some patients may still need procedures such as bone grafting or a sinus lift — only an in-person examination with imaging can confirm this.",
          ],
          fr: [
            "L’All-on-6 utilise six implants et peut offrir un soutien supplémentaire dans certains cas. Le bon choix dépend de votre os, de votre cas et de l’évaluation de votre dentiste. Certains patients peuvent encore nécessiter une greffe osseuse ou une élévation du sinus — seul un examen en personne avec imagerie peut le confirmer.",
          ],
          de: [
            "All-on-6 nutzt sechs Implantate und kann in manchen Fällen mehr Halt bieten. Die richtige Wahl hängt von Knochen, Fall und der Beurteilung des Zahnarztes ab. Manche Patienten benötigen dennoch Eingriffe wie Knochenaufbau oder Sinuslift — nur eine persönliche Untersuchung mit Bildgebung kann dies bestätigen.",
          ],
        },
      },
      {
        h: {
          en: "Questions to ask before All-on-4 treatment",
          fr: "Questions à poser avant un traitement All-on-4",
          de: "Fragen vor einer All-on-4-Behandlung",
        },
        list: {
          en: [
            "Which implant system and prosthesis materials will be used?",
            "How experienced is the dentist with full-arch cases?",
            "How many visits and how long between stages?",
            "What are the warranty terms and the aftercare plan?",
          ],
          fr: [
            "Quel système d’implant et quels matériaux de prothèse seront utilisés ?",
            "Quelle est l’expérience du dentiste en cas d’arcade complète ?",
            "Combien de visites et quel délai entre les étapes ?",
            "Quelles sont les conditions de garantie et le plan de suivi ?",
          ],
          de: [
            "Welches Implantatsystem und welche Prothesenmaterialien werden verwendet?",
            "Wie erfahren ist der Zahnarzt mit Vollbogen-Fällen?",
            "Wie viele Besuche und welcher Abstand zwischen den Phasen?",
            "Wie lauten Garantiebedingungen und Nachsorgeplan?",
          ],
        },
      },
    ],
    related: ["all-on-6", "dental-implants", "dentures", "smile-makeover"],
  },

  {
    key: "all-on-6",
    icon: "Grid3x3",
    featured: false,
    image:
      "https://images.unsplash.com/photo-1609840114035-3c981b782dfe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "all-on-6-dental-implants-vietnam",
      fr: "all-on-6-implants-dentaires-vietnam",
      de: "all-on-6-zahnimplantate-vietnam",
    },
    name: { en: "All-on-6 Dental Implants", fr: "Implants All-on-6", de: "All-on-6 Implantate" },
    tagline: {
      en: "A full arch supported by six implants.",
      fr: "Une arcade complète soutenue par six implants.",
      de: "Ein ganzer Zahnbogen auf sechs Implantaten.",
    },
    metaTitle: {
      en: "All-on-6 Dental Implants in Vietnam | Patient Guide",
      fr: "Implants All-on-6 au Vietnam | Guide du patient",
      de: "All-on-6 Zahnimplantate in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "Independent information on All-on-6 dental implants in Vietnam and how they differ from All-on-4.",
      fr: "Informations indépendantes sur les implants All-on-6 au Vietnam et leurs différences avec l’All-on-4.",
      de: "Unabhängige Informationen zu All-on-6 Implantaten in Vietnam und Unterschiede zu All-on-4.",
    },
    overview: {
      en: [
        "All-on-6 uses six implants to support a full arch of fixed replacement teeth. The additional implants may offer extra support in certain cases.",
        "As with any implant treatment, suitability depends on individual factors and must be assessed by a licensed dentist.",
      ],
      fr: [
        "L’All-on-6 utilise six implants pour soutenir une arcade complète de dents fixes. Les implants supplémentaires peuvent offrir un soutien accru dans certains cas.",
        "Comme pour tout traitement implantaire, l’indication dépend de facteurs individuels et doit être évaluée par un dentiste agréé.",
      ],
      de: [
        "All-on-6 nutzt sechs Implantate, um einen ganzen Bogen festsitzender Ersatzzähne zu tragen. Die zusätzlichen Implantate können in bestimmten Fällen mehr Halt bieten.",
        "Wie bei jeder Implantatbehandlung hängt die Eignung von individuellen Faktoren ab und muss von einem lizenzierten Zahnarzt beurteilt werden.",
      ],
    },
    involves: {
      en: [
        "Assessment and 3D imaging of the jaw",
        "Placement of six implants",
        "Provisional teeth in many cases",
        "A final fixed bridge after healing",
      ],
      fr: [
        "Évaluation et imagerie 3D de la mâchoire",
        "Pose de six implants",
        "Dents provisoires dans de nombreux cas",
        "Un bridge fixe définitif après cicatrisation",
      ],
      de: [
        "Beurteilung und 3D-Bildgebung des Kiefers",
        "Setzen von sechs Implantaten",
        "In vielen Fällen provisorische Zähne",
        "Eine finale festsitzende Brücke nach der Einheilung",
      ],
    },
    benefits: {
      en: [
        "Full-arch fixed replacement teeth",
        "Additional implant support in suitable cases",
        "Designed to restore function and appearance",
        "A long-term option when well maintained",
      ],
      fr: [
        "Dents fixes d’arcade complète",
        "Soutien implantaire supplémentaire dans les cas adaptés",
        "Conçu pour restaurer fonction et esthétique",
        "Une option durable avec un bon entretien",
      ],
      de: [
        "Festsitzender Zahnersatz für den ganzen Bogen",
        "Zusätzlicher Implantathalt in geeigneten Fällen",
        "Soll Funktion und Aussehen wiederherstellen",
        "Bei guter Pflege eine langfristige Option",
      ],
    },
    faqs: [
      {
        q: {
          en: "What is the difference between All-on-4 and All-on-6?",
          fr: "Quelle est la différence entre All-on-4 et All-on-6 ?",
          de: "Was ist der Unterschied zwischen All-on-4 und All-on-6?",
        },
        a: {
          en: "The main difference is the number of implants used to support the arch. The right choice depends on your bone, your case and your dentist’s assessment.",
          fr: "La principale différence est le nombre d’implants soutenant l’arcade. Le bon choix dépend de votre os, de votre cas et de l’évaluation de votre dentiste.",
          de: "Der Hauptunterschied ist die Anzahl der Implantate, die den Bogen tragen. Die richtige Wahl hängt von Ihrem Knochen, Ihrem Fall und der Beurteilung des Zahnarztes ab.",
        },
      },
    ],
    related: ["all-on-4", "dental-implants", "dentures"],
  },

  {
    key: "dental-crowns",
    icon: "Crown",
    featured: false,
    image:
      "https://images.unsplash.com/photo-1615916609315-99a3f2f186f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "dental-crowns-vietnam",
      fr: "couronnes-dentaires-vietnam",
      de: "zahnkronen-vietnam",
    },
    name: { en: "Dental Crowns", fr: "Couronnes dentaires", de: "Zahnkronen" },
    tagline: {
      en: "Custom caps that restore damaged teeth.",
      fr: "Des coiffes sur mesure qui restaurent les dents abîmées.",
      de: "Maßgefertigte Kappen zur Wiederherstellung beschädigter Zähne.",
    },
    metaTitle: {
      en: "Dental Crowns in Vietnam | Patient Guide",
      fr: "Couronnes dentaires au Vietnam | Guide du patient",
      de: "Zahnkronen in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "What dental crowns are, the materials available and what international patients should check in Vietnam.",
      fr: "Ce que sont les couronnes dentaires, les matériaux disponibles et les points à vérifier au Vietnam.",
      de: "Was Zahnkronen sind, welche Materialien es gibt und worauf internationale Patienten in Vietnam achten sollten.",
    },
    overview: {
      en: [
        "A dental crown is a custom-made cap placed over a damaged or weakened tooth to restore its shape, strength and appearance. Crowns are also used to finish root canal treatment or to complete an implant.",
        "Materials include porcelain, ceramic and zirconia. Your dentist will recommend the most appropriate option for your tooth.",
      ],
      fr: [
        "Une couronne dentaire est une coiffe sur mesure placée sur une dent abîmée ou fragilisée pour restaurer sa forme, sa solidité et son apparence. Les couronnes servent aussi à finaliser un traitement de canal ou un implant.",
        "Les matériaux incluent la porcelaine, la céramique et la zircone. Votre dentiste recommandera l’option la plus adaptée à votre dent.",
      ],
      de: [
        "Eine Zahnkrone ist eine maßgefertigte Kappe über einem beschädigten oder geschwächten Zahn, die Form, Stabilität und Aussehen wiederherstellt. Kronen schließen auch eine Wurzelbehandlung oder ein Implantat ab.",
        "Materialien sind u. a. Porzellan, Keramik und Zirkon. Ihr Zahnarzt empfiehlt die passende Option für Ihren Zahn.",
      ],
    },
    involves: {
      en: [
        "Examination and shade selection",
        "Preparation of the tooth",
        "Impressions or a digital scan",
        "Fitting of the custom crown",
      ],
      fr: [
        "Examen et choix de la teinte",
        "Préparation de la dent",
        "Empreintes ou scan numérique",
        "Pose de la couronne sur mesure",
      ],
      de: [
        "Untersuchung und Farbauswahl",
        "Vorbereitung des Zahns",
        "Abdrücke oder digitaler Scan",
        "Einsetzen der individuellen Krone",
      ],
    },
    benefits: {
      en: [
        "Restores a damaged or weak tooth",
        "Custom-matched to your natural teeth",
        "Protects a tooth after root canal treatment",
        "Durable with good oral care",
      ],
      fr: [
        "Restaure une dent abîmée ou fragile",
        "Assortie à vos dents naturelles",
        "Protège une dent après un traitement de canal",
        "Durable avec une bonne hygiène",
      ],
      de: [
        "Stellt einen beschädigten oder schwachen Zahn wieder her",
        "Individuell an die natürlichen Zähne angepasst",
        "Schützt einen Zahn nach der Wurzelbehandlung",
        "Langlebig bei guter Mundpflege",
      ],
    },
    faqs: [
      {
        q: {
          en: "Which crown material is best?",
          fr: "Quel matériau de couronne est le meilleur ?",
          de: "Welches Kronenmaterial ist am besten?",
        },
        a: {
          en: "There is no single best material — it depends on the tooth’s position, function and appearance needs. Your dentist will recommend an option for your case.",
          fr: "Il n’existe pas de matériau unique idéal — cela dépend de la position, de la fonction et de l’esthétique. Votre dentiste recommandera une option adaptée.",
          de: "Es gibt kein einziges bestes Material — es hängt von Position, Funktion und Ästhetik ab. Ihr Zahnarzt empfiehlt die passende Option.",
        },
      },
    ],
    related: ["porcelain-veneers", "root-canal", "dental-bridges", "smile-makeover"],
  },

  {
    key: "porcelain-veneers",
    icon: "Sparkles",
    featured: true,
    image:
      "https://images.unsplash.com/photo-1581585504841-6c1c1cf4b0d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "veneers-vietnam",
      fr: "facettes-dentaires-vietnam",
      de: "veneers-vietnam",
    },
    name: { en: "Porcelain Veneers", fr: "Facettes en porcelaine", de: "Porzellan-Veneers" },
    tagline: {
      en: "Thin shells that refine the look of front teeth.",
      fr: "De fines coques qui affinent l’aspect des dents antérieures.",
      de: "Dünne Schalen, die das Aussehen der Frontzähne verfeinern.",
    },
    metaTitle: {
      en: "Porcelain Veneers in Vietnam | Patient Guide",
      fr: "Facettes dentaires au Vietnam | Guide du patient",
      de: "Veneers in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "Independent information on porcelain veneers in Vietnam: what they are, what they involve and what to consider.",
      fr: "Informations indépendantes sur les facettes en porcelaine au Vietnam : définition, déroulement et points à considérer.",
      de: "Unabhängige Informationen zu Porzellan-Veneers in Vietnam: Was sie sind, der Ablauf und Überlegungen.",
    },
    overview: {
      en: [
        "Veneers are thin, custom-made shells bonded to the front of teeth to improve their shape, colour or alignment. They are a cosmetic option often chosen for the smile line.",
        "Veneers usually involve some preparation of the tooth surface and are irreversible, so careful planning with a dentist is important.",
      ],
      fr: [
        "Les facettes sont de fines coques sur mesure collées à l’avant des dents pour améliorer leur forme, leur couleur ou leur alignement. C’est une option esthétique souvent choisie pour le sourire.",
        "Les facettes impliquent généralement une préparation de la surface de la dent et sont irréversibles ; une planification soignée avec un dentiste est donc importante.",
      ],
      de: [
        "Veneers sind dünne, maßgefertigte Schalen, die auf die Vorderseite der Zähne geklebt werden, um Form, Farbe oder Stellung zu verbessern. Sie sind eine ästhetische Option für die Frontzähne.",
        "Veneers erfordern meist eine gewisse Vorbereitung der Zahnoberfläche und sind irreversibel; sorgfältige Planung mit dem Zahnarzt ist daher wichtig.",
      ],
    },
    involves: {
      en: [
        "Cosmetic consultation and smile planning",
        "Light preparation of the tooth surface",
        "Impressions or a digital scan",
        "Bonding of the custom veneers",
      ],
      fr: [
        "Consultation esthétique et planification du sourire",
        "Préparation légère de la surface de la dent",
        "Empreintes ou scan numérique",
        "Collage des facettes sur mesure",
      ],
      de: [
        "Ästhetische Beratung und Smile-Planung",
        "Leichte Vorbereitung der Zahnoberfläche",
        "Abdrücke oder digitaler Scan",
        "Aufkleben der individuellen Veneers",
      ],
    },
    benefits: {
      en: [
        "Can improve colour, shape and alignment",
        "Custom-designed for a natural look",
        "Stain-resistant porcelain options",
        "A popular choice for smile enhancement",
      ],
      fr: [
        "Peut améliorer couleur, forme et alignement",
        "Conçues sur mesure pour un rendu naturel",
        "Options en porcelaine résistantes aux taches",
        "Un choix populaire pour embellir le sourire",
      ],
      de: [
        "Kann Farbe, Form und Stellung verbessern",
        "Individuell für ein natürliches Aussehen gestaltet",
        "Fleckenresistente Porzellan-Optionen",
        "Beliebt zur Verschönerung des Lächelns",
      ],
    },
    faqs: [
      {
        q: {
          en: "Are veneers permanent?",
          fr: "Les facettes sont-elles permanentes ?",
          de: "Sind Veneers dauerhaft?",
        },
        a: {
          en: "Because a small amount of enamel is usually removed, veneers are considered irreversible and will eventually need replacement. Discuss longevity and alternatives with your dentist.",
          fr: "Comme une petite quantité d’émail est généralement retirée, les facettes sont considérées comme irréversibles et devront être remplacées à terme. Discutez de leur durée et des alternatives avec votre dentiste.",
          de: "Da meist etwas Schmelz entfernt wird, gelten Veneers als irreversibel und müssen irgendwann ersetzt werden. Besprechen Sie Haltbarkeit und Alternativen mit Ihrem Zahnarzt.",
        },
      },
    ],
    related: ["smile-makeover", "dental-crowns", "teeth-whitening", "dental-implants"],
  },

  {
    key: "smile-makeover",
    icon: "Smile",
    featured: false,
    image:
      "https://images.unsplash.com/photo-1607613009820-a29f7bb81c04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "smile-makeover-vietnam",
      fr: "relooking-sourire-vietnam",
      de: "smile-makeover-vietnam",
    },
    name: { en: "Smile Makeover", fr: "Relooking du sourire", de: "Smile Makeover" },
    tagline: {
      en: "A combined plan to improve your smile.",
      fr: "Un plan combiné pour améliorer votre sourire.",
      de: "Ein kombinierter Plan zur Verbesserung Ihres Lächelns.",
    },
    metaTitle: {
      en: "Smile Makeover in Vietnam | Patient Guide",
      fr: "Relooking du sourire au Vietnam | Guide du patient",
      de: "Smile Makeover in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "How a smile makeover combines treatments such as veneers, crowns and whitening, and what to consider in Vietnam.",
      fr: "Comment un relooking du sourire combine facettes, couronnes et blanchiment, et ce qu’il faut considérer au Vietnam.",
      de: "Wie ein Smile Makeover Veneers, Kronen und Bleaching kombiniert und was in Vietnam zu beachten ist.",
    },
    overview: {
      en: [
        "A smile makeover is a personalised plan that may combine several treatments — such as veneers, crowns, whitening or implants — to improve the overall appearance of your smile.",
        "The right combination depends on your goals and your dentist’s assessment of what is achievable and healthy for your teeth.",
      ],
      fr: [
        "Un relooking du sourire est un plan personnalisé pouvant combiner plusieurs traitements — facettes, couronnes, blanchiment ou implants — pour améliorer l’aspect global du sourire.",
        "La bonne combinaison dépend de vos objectifs et de l’évaluation de votre dentiste quant à ce qui est réalisable et sain pour vos dents.",
      ],
      de: [
        "Ein Smile Makeover ist ein individueller Plan, der mehrere Behandlungen kombinieren kann — z. B. Veneers, Kronen, Bleaching oder Implantate — um das Gesamtbild des Lächelns zu verbessern.",
        "Die richtige Kombination hängt von Ihren Zielen und der Einschätzung des Zahnarztes ab, was gesund und machbar ist.",
      ],
    },
    involves: {
      en: [
        "Detailed smile consultation and goals",
        "A tailored combination of treatments",
        "Preview or digital planning where available",
        "Staged treatment and final result",
      ],
      fr: [
        "Consultation détaillée et objectifs du sourire",
        "Une combinaison de traitements sur mesure",
        "Aperçu ou planification numérique si disponible",
        "Traitement échelonné et résultat final",
      ],
      de: [
        "Ausführliche Smile-Beratung und Ziele",
        "Eine maßgeschneiderte Kombination von Behandlungen",
        "Vorschau oder digitale Planung, falls verfügbar",
        "Stufenweise Behandlung und Endergebnis",
      ],
    },
    benefits: {
      en: [
        "A coordinated, personalised plan",
        "Addresses multiple concerns together",
        "Designed around your facial features",
        "Clear expectations before you begin",
      ],
      fr: [
        "Un plan coordonné et personnalisé",
        "Traite plusieurs aspects à la fois",
        "Conçu en fonction de vos traits",
        "Des attentes claires avant de commencer",
      ],
      de: [
        "Ein abgestimmter, individueller Plan",
        "Behandelt mehrere Anliegen zugleich",
        "Auf Ihre Gesichtszüge abgestimmt",
        "Klare Erwartungen vor dem Start",
      ],
    },
    faqs: [
      {
        q: {
          en: "How is a smile makeover planned?",
          fr: "Comment planifie-t-on un relooking du sourire ?",
          de: "Wie wird ein Smile Makeover geplant?",
        },
        a: {
          en: "It begins with a consultation to understand your goals, followed by an assessment and a tailored plan. Some clinics offer digital previews. Only a dentist can confirm what is suitable.",
          fr: "Cela commence par une consultation pour comprendre vos objectifs, suivie d’une évaluation et d’un plan sur mesure. Certaines cliniques proposent des aperçus numériques. Seul un dentiste peut confirmer l’indication.",
          de: "Es beginnt mit einer Beratung zu Ihren Zielen, gefolgt von einer Beurteilung und einem individuellen Plan. Manche Kliniken bieten digitale Vorschauen. Nur ein Zahnarzt kann die Eignung bestätigen.",
        },
      },
    ],
    related: ["porcelain-veneers", "teeth-whitening", "dental-crowns", "dental-implants"],
  },

  {
    key: "dental-bridges",
    icon: "GitCommitHorizontal",
    featured: false,
    image:
      "https://images.unsplash.com/photo-1598256989800-fe5f95da9787?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "dental-bridges-vietnam",
      fr: "bridges-dentaires-vietnam",
      de: "zahnbruecken-vietnam",
    },
    name: { en: "Dental Bridges", fr: "Bridges dentaires", de: "Zahnbrücken" },
    tagline: {
      en: "Fixed replacements that bridge a gap.",
      fr: "Des remplacements fixes qui comblent un espace.",
      de: "Festsitzender Ersatz, der eine Lücke überbrückt.",
    },
    metaTitle: {
      en: "Dental Bridges in Vietnam | Patient Guide",
      fr: "Bridges dentaires au Vietnam | Guide du patient",
      de: "Zahnbrücken in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "What dental bridges are and how they compare to implants for replacing missing teeth.",
      fr: "Ce que sont les bridges dentaires et comment ils se comparent aux implants pour remplacer les dents manquantes.",
      de: "Was Zahnbrücken sind und wie sie sich im Vergleich zu Implantaten für fehlende Zähne verhalten.",
    },
    overview: {
      en: [
        "A dental bridge replaces one or more missing teeth by joining an artificial tooth to the natural teeth or implants on either side of the gap.",
        "Bridges are one option for replacing missing teeth. Your dentist can explain how a bridge compares to an implant for your situation.",
      ],
      fr: [
        "Un bridge dentaire remplace une ou plusieurs dents manquantes en reliant une dent artificielle aux dents naturelles ou aux implants situés de part et d’autre de l’espace.",
        "Le bridge est une option parmi d’autres. Votre dentiste peut expliquer comment il se compare à un implant dans votre cas.",
      ],
      de: [
        "Eine Zahnbrücke ersetzt einen oder mehrere fehlende Zähne, indem ein künstlicher Zahn mit den natürlichen Zähnen oder Implantaten beidseits der Lücke verbunden wird.",
        "Eine Brücke ist eine Option für fehlende Zähne. Ihr Zahnarzt erklärt, wie sie sich in Ihrer Situation zum Implantat verhält.",
      ],
    },
    involves: {
      en: [
        "Assessment of the gap and supporting teeth",
        "Preparation of the supporting teeth",
        "Impressions or a digital scan",
        "Fitting of the custom bridge",
      ],
      fr: [
        "Évaluation de l’espace et des dents supports",
        "Préparation des dents supports",
        "Empreintes ou scan numérique",
        "Pose du bridge sur mesure",
      ],
      de: [
        "Beurteilung der Lücke und der Pfeilerzähne",
        "Vorbereitung der Pfeilerzähne",
        "Abdrücke oder digitaler Scan",
        "Einsetzen der individuellen Brücke",
      ],
    },
    benefits: {
      en: [
        "Replaces missing teeth without surgery",
        "Restores chewing and appearance",
        "A fixed, non-removable option",
        "Often completed in fewer visits than implants",
      ],
      fr: [
        "Remplace des dents manquantes sans chirurgie",
        "Restaure la mastication et l’esthétique",
        "Une option fixe et non amovible",
        "Souvent moins de visites qu’un implant",
      ],
      de: [
        "Ersetzt fehlende Zähne ohne Operation",
        "Stellt Kaufunktion und Aussehen wieder her",
        "Eine festsitzende, nicht herausnehmbare Option",
        "Oft weniger Besuche als bei Implantaten",
      ],
    },
    faqs: [
      {
        q: {
          en: "Bridge or implant — which is better?",
          fr: "Bridge ou implant — lequel choisir ?",
          de: "Brücke oder Implantat — was ist besser?",
        },
        a: {
          en: "Both have advantages. Implants avoid altering neighbouring teeth, while bridges may involve fewer visits. The best option depends on your teeth, bone and preferences, as assessed by a dentist.",
          fr: "Les deux ont des avantages. L’implant évite de modifier les dents voisines, le bridge peut nécessiter moins de visites. Le meilleur choix dépend de votre cas, évalué par un dentiste.",
          de: "Beide haben Vorteile. Implantate vermeiden das Verändern der Nachbarzähne, Brücken erfordern evtl. weniger Besuche. Die beste Option hängt von Ihrem Fall ab, beurteilt vom Zahnarzt.",
        },
      },
    ],
    related: ["dental-implants", "dental-crowns", "dentures"],
  },

  {
    key: "dentures",
    icon: "Layers",
    featured: false,
    image:
      "https://images.unsplash.com/photo-1620912189865-1e8a1f8b0e0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "dentures-vietnam",
      fr: "protheses-dentaires-vietnam",
      de: "zahnprothesen-vietnam",
    },
    name: { en: "Dentures", fr: "Prothèses dentaires", de: "Zahnprothesen" },
    tagline: {
      en: "Removable replacements for missing teeth.",
      fr: "Des remplacements amovibles pour les dents manquantes.",
      de: "Herausnehmbarer Ersatz für fehlende Zähne.",
    },
    metaTitle: {
      en: "Dentures in Vietnam | Patient Guide",
      fr: "Prothèses dentaires au Vietnam | Guide du patient",
      de: "Zahnprothesen in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "Full and partial dentures explained for international patients considering treatment in Vietnam.",
      fr: "Prothèses complètes et partielles expliquées pour les patients internationaux au Vietnam.",
      de: "Voll- und Teilprothesen erklärt für internationale Patienten in Vietnam.",
    },
    overview: {
      en: [
        "Dentures are removable appliances that replace missing teeth and surrounding tissue. They can be full (replacing all teeth in an arch) or partial (replacing some teeth).",
        "Implant-supported dentures are another option that can add stability. Your dentist will advise what suits your needs.",
      ],
      fr: [
        "Les prothèses sont des appareils amovibles qui remplacent les dents manquantes et les tissus environnants. Elles peuvent être complètes (toute l’arcade) ou partielles (certaines dents).",
        "Les prothèses sur implants sont une autre option qui peut améliorer la stabilité. Votre dentiste vous conseillera selon vos besoins.",
      ],
      de: [
        "Prothesen sind herausnehmbare Apparaturen, die fehlende Zähne und umliegendes Gewebe ersetzen. Sie können vollständig (ganzer Bogen) oder teilweise (einige Zähne) sein.",
        "Implantatgetragene Prothesen sind eine weitere Option für mehr Halt. Ihr Zahnarzt berät Sie je nach Bedarf.",
      ],
    },
    involves: {
      en: [
        "Assessment and impressions",
        "Custom fabrication of the denture",
        "Fitting and adjustments",
        "Guidance on care and use",
      ],
      fr: [
        "Évaluation et empreintes",
        "Fabrication sur mesure de la prothèse",
        "Pose et ajustements",
        "Conseils d’entretien et d’utilisation",
      ],
      de: [
        "Beurteilung und Abdrücke",
        "Individuelle Herstellung der Prothese",
        "Anpassung und Korrekturen",
        "Hinweise zu Pflege und Gebrauch",
      ],
    },
    benefits: {
      en: [
        "Replaces several or all missing teeth",
        "A non-surgical option",
        "Can restore appearance and chewing",
        "Implant-supported options for added stability",
      ],
      fr: [
        "Remplace plusieurs ou toutes les dents manquantes",
        "Une option non chirurgicale",
        "Peut restaurer l’esthétique et la mastication",
        "Options sur implants pour plus de stabilité",
      ],
      de: [
        "Ersetzt mehrere oder alle fehlenden Zähne",
        "Eine nicht-chirurgische Option",
        "Kann Aussehen und Kaufunktion wiederherstellen",
        "Implantatgetragene Optionen für mehr Halt",
      ],
    },
    faqs: [
      {
        q: {
          en: "Can dentures be made in a short trip?",
          fr: "Une prothèse peut-elle être réalisée en un court séjour ?",
          de: "Können Prothesen in einer kurzen Reise gefertigt werden?",
        },
        a: {
          en: "Timelines depend on the type of denture and the clinic’s workflow. Some dentures require several appointments for fitting and adjustment. Ask your provider about timing.",
          fr: "Les délais dépendent du type de prothèse et de l’organisation de la clinique. Certaines nécessitent plusieurs rendez-vous pour l’ajustement. Renseignez-vous auprès du prestataire.",
          de: "Die Dauer hängt von Prothesentyp und Klinikablauf ab. Manche Prothesen erfordern mehrere Termine zur Anpassung. Fragen Sie Ihren Anbieter.",
        },
      },
    ],
    related: ["dental-implants", "all-on-4", "dental-bridges"],
  },

  {
    key: "root-canal",
    icon: "Activity",
    featured: false,
    image:
      "https://images.unsplash.com/photo-1626736985484-1e3d3d3d3d3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "root-canal-vietnam",
      fr: "traitement-canalaire-vietnam",
      de: "wurzelbehandlung-vietnam",
    },
    name: { en: "Root Canal Treatment", fr: "Traitement de canal", de: "Wurzelbehandlung" },
    tagline: {
      en: "Treatment to save an infected tooth.",
      fr: "Un traitement pour sauver une dent infectée.",
      de: "Behandlung zur Rettung eines entzündeten Zahns.",
    },
    metaTitle: {
      en: "Root Canal Treatment in Vietnam | Patient Guide",
      fr: "Traitement de canal au Vietnam | Guide du patient",
      de: "Wurzelbehandlung in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "What root canal treatment involves and how it helps save a damaged or infected tooth.",
      fr: "En quoi consiste le traitement de canal et comment il permet de sauver une dent abîmée ou infectée.",
      de: "Was eine Wurzelbehandlung beinhaltet und wie sie einen geschädigten oder entzündeten Zahn rettet.",
    },
    overview: {
      en: [
        "Root canal treatment removes infected or damaged tissue from inside a tooth, then cleans, fills and seals it. It is used to relieve pain and save a tooth that might otherwise need removal.",
        "A crown is often recommended afterwards to protect the treated tooth.",
      ],
      fr: [
        "Le traitement de canal retire le tissu infecté ou abîmé à l’intérieur d’une dent, puis la nettoie, l’obture et la scelle. Il soulage la douleur et sauve une dent qui devrait sinon être extraite.",
        "Une couronne est souvent recommandée ensuite pour protéger la dent traitée.",
      ],
      de: [
        "Eine Wurzelbehandlung entfernt entzündetes oder geschädigtes Gewebe aus dem Zahninneren, reinigt, füllt und versiegelt es. Sie lindert Schmerzen und rettet einen Zahn, der sonst entfernt werden müsste.",
        "Anschließend wird oft eine Krone empfohlen, um den behandelten Zahn zu schützen.",
      ],
    },
    involves: {
      en: [
        "Examination and X-ray of the tooth",
        "Removal of infected tissue",
        "Cleaning, filling and sealing the canals",
        "A crown or restoration to protect the tooth",
      ],
      fr: [
        "Examen et radiographie de la dent",
        "Retrait du tissu infecté",
        "Nettoyage, obturation et scellement des canaux",
        "Une couronne ou restauration pour protéger la dent",
      ],
      de: [
        "Untersuchung und Röntgen des Zahns",
        "Entfernen des entzündeten Gewebes",
        "Reinigen, Füllen und Versiegeln der Kanäle",
        "Krone oder Restauration zum Schutz des Zahns",
      ],
    },
    benefits: {
      en: [
        "Can relieve pain from infection",
        "Helps save a natural tooth",
        "Restores normal biting and chewing",
        "A well-established treatment",
      ],
      fr: [
        "Peut soulager la douleur due à l’infection",
        "Aide à sauver une dent naturelle",
        "Restaure la mastication normale",
        "Un traitement bien établi",
      ],
      de: [
        "Kann Schmerzen durch Entzündung lindern",
        "Hilft, einen natürlichen Zahn zu erhalten",
        "Stellt normales Beißen und Kauen wieder her",
        "Eine bewährte Behandlung",
      ],
    },
    faqs: [
      {
        q: {
          en: "Is root canal treatment painful?",
          fr: "Le traitement de canal est-il douloureux ?",
          de: "Ist eine Wurzelbehandlung schmerzhaft?",
        },
        a: {
          en: "Modern root canal treatment is usually carried out under local anaesthetic and is often compared to having a filling. Discuss any concerns with your dentist.",
          fr: "Le traitement de canal moderne se fait généralement sous anesthésie locale et est souvent comparé à la pose d’un plombage. Parlez-en à votre dentiste.",
          de: "Eine moderne Wurzelbehandlung erfolgt meist unter örtlicher Betäubung und wird oft mit einer Füllung verglichen. Besprechen Sie Bedenken mit Ihrem Zahnarzt.",
        },
      },
    ],
    related: ["dental-crowns", "dental-implants", "dental-bridges"],
  },

  {
    key: "teeth-whitening",
    icon: "Sun",
    featured: false,
    image:
      "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    slug: {
      en: "teeth-whitening-vietnam",
      fr: "blanchiment-dentaire-vietnam",
      de: "zahnaufhellung-vietnam",
    },
    name: { en: "Teeth Whitening", fr: "Blanchiment dentaire", de: "Zahnaufhellung" },
    tagline: {
      en: "A simple way to brighten your smile.",
      fr: "Un moyen simple d’éclaircir votre sourire.",
      de: "Eine einfache Möglichkeit, Ihr Lächeln aufzuhellen.",
    },
    metaTitle: {
      en: "Teeth Whitening in Vietnam | Patient Guide",
      fr: "Blanchiment dentaire au Vietnam | Guide du patient",
      de: "Zahnaufhellung in Vietnam | Patientenratgeber",
    },
    metaDesc: {
      en: "Professional teeth whitening explained for international patients considering treatment in Vietnam.",
      fr: "Le blanchiment dentaire professionnel expliqué pour les patients internationaux au Vietnam.",
      de: "Professionelle Zahnaufhellung erklärt für internationale Patienten in Vietnam.",
    },
    overview: {
      en: [
        "Professional teeth whitening lightens the shade of natural teeth using a controlled whitening agent, either in the clinic or with a take-home kit prescribed by the dentist.",
        "Whitening works on natural teeth only and does not change the colour of crowns or veneers. A dental check is recommended first.",
      ],
      fr: [
        "Le blanchiment dentaire professionnel éclaircit la teinte des dents naturelles à l’aide d’un agent contrôlé, au cabinet ou avec un kit à domicile prescrit par le dentiste.",
        "Le blanchiment n’agit que sur les dents naturelles et ne modifie pas la couleur des couronnes ou facettes. Un contrôle dentaire est recommandé au préalable.",
      ],
      de: [
        "Professionelle Zahnaufhellung hellt die Farbe natürlicher Zähne mit einem kontrollierten Aufhellungsmittel auf — in der Praxis oder mit einem vom Zahnarzt verordneten Heim-Set.",
        "Bleaching wirkt nur auf natürliche Zähne und verändert die Farbe von Kronen oder Veneers nicht. Eine zahnärztliche Kontrolle wird zuvor empfohlen.",
      ],
    },
    involves: {
      en: [
        "A dental check before whitening",
        "Selection of in-clinic or take-home whitening",
        "Application of a controlled whitening agent",
        "Advice on maintaining results",
      ],
      fr: [
        "Un contrôle dentaire avant le blanchiment",
        "Choix entre blanchiment au cabinet ou à domicile",
        "Application d’un agent contrôlé",
        "Conseils pour maintenir les résultats",
      ],
      de: [
        "Eine zahnärztliche Kontrolle vor dem Bleaching",
        "Wahl zwischen Praxis- oder Heim-Bleaching",
        "Auftragen eines kontrollierten Aufhellungsmittels",
        "Tipps zum Erhalt der Ergebnisse",
      ],
    },
    benefits: {
      en: [
        "Can brighten the natural tooth shade",
        "A non-invasive cosmetic option",
        "Professionally supervised",
        "Quick compared with other treatments",
      ],
      fr: [
        "Peut éclaircir la teinte naturelle des dents",
        "Une option esthétique non invasive",
        "Supervisé par un professionnel",
        "Rapide par rapport à d’autres traitements",
      ],
      de: [
        "Kann die natürliche Zahnfarbe aufhellen",
        "Eine nicht-invasive ästhetische Option",
        "Professionell überwacht",
        "Schnell im Vergleich zu anderen Behandlungen",
      ],
    },
    faqs: [
      {
        q: {
          en: "How long do whitening results last?",
          fr: "Combien de temps durent les résultats du blanchiment ?",
          de: "Wie lange halten die Bleaching-Ergebnisse?",
        },
        a: {
          en: "Results vary and can fade over time depending on diet and habits. Your dentist can advise on maintenance and whether whitening is suitable for you.",
          fr: "Les résultats varient et peuvent s’estomper selon l’alimentation et les habitudes. Votre dentiste peut conseiller sur l’entretien et l’indication.",
          de: "Die Ergebnisse variieren und können je nach Ernährung und Gewohnheiten verblassen. Ihr Zahnarzt berät zu Pflege und Eignung.",
        },
      },
    ],
    related: ["porcelain-veneers", "smile-makeover", "dental-crowns"],
  },
];

export const getTreatment = (key) => TREATMENTS.find((t) => t.key === key);
export const getTreatmentBySlug = (slug, lang) =>
  TREATMENTS.find((t) => t.slug[lang] === slug || Object.values(t.slug).includes(slug));
